import { zodResolver } from "@hookform/resolvers/zod";
import EditNoteIcon from "@mui/icons-material/EditNote";
import InventoryIcon from "@mui/icons-material/Inventory";
import LoadingButton from "@mui/lab/LoadingButton";
import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Grid,
    Skeleton,
    TextField,
    Typography,
    InputAdornment,
    IconButton
} from "@mui/material";
import { t } from "i18next";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { SnackbarSuccess } from "../../../components/base/SnackbarAlert/SnackbarSuccess";
import { connectedCompanyPersonFormSchema } from "../../../components/dialog/AddConnectedCompanyPerson/AddConnectedCompanyPerson";
import { ClientFilesDialog } from "../../../components/dialog/ClientFilesDialog/ClientFilesDialog";
import { ControlledSelect } from "../../../components/form/ControlledSelect/ControlledSelect";
import { COMPANY_LEGAL_TYPES, FORM_ERR } from "../../../helpers/constants";
import { useDialogBetter } from "../../../hooks/useDialogBetter";
import { useToasts } from "../../../hooks/useToasts";
import { useEditCustomerCompanyMutation } from "../../../services/customersService";
import { CompanyAddConnectedPersons } from "./CompanyAddConnectedPersons";
import { AddressCopyButton } from "../../../components/buttons/AddressCopyButton";
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import { AdditionalPhonesDialog } from "./AdditionalPhonesDialog/AdditionalPhonesDialog";
import MailIcon from '@mui/icons-material/Mail';
import { AdditionalEmailsDialog } from "./AdditionalEmailsDialog/AdditionalEmailsDialog";

export const CounterpartyInfoForm = (props) => {
    const { counterpartyId, counterpartyData, isLoading } = props;

    const { t } = useTranslation();

    const { mutate: editCompany, isPending } = useEditCustomerCompanyMutation({
        onSuccess: () => {
            toast.custom(
                () => (
                    <SnackbarSuccess
                        message={t("clients.passportDialogCompany.savedSuccessfully")}
                    />
                ),
                {
                    position: "bottom-right",
                },
            );
        },
    });

    const { onFormErrors } = useToasts();
    const { openDialog: openClientFilesDialog } = useDialogBetter();
    const { openDialog: openAdditionalPhonesDialog } = useDialogBetter();
    const { openDialog: openAdditionalEmailsDialog } = useDialogBetter();

    const { handleSubmit, control, formState, register, reset, setValue, watch } = useForm({
        resolver: zodResolver(counterpartyPassportFormSchema),
        defaultValues: {},
    });

    // Update form values when customer data is loaded
    useEffect(() => {
        if (!counterpartyData) {
            return;
        }

        reset(formDataFromCounterparty(counterpartyData));
    }, [counterpartyData, reset]);

    const enabledBtn = watch([
        "companyName",
        "companyShortName",
        "companyType",
        "nip",
        "email",
    ]).reduce((acc, val) => acc && !!val, true);

    const onSubmit = (data) => {
        console.log("Submitting data:", data);
        editCompany(formDataToRequestBody(counterpartyId, data));
        // props.onClose(); // TODO: wiadomosci o sukcesach/errorach
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    const connectedPersonCustomerIds =
        watch("connectedPersons")
            ?.map((person) => person.existingClientId)
            .filter((id) => !!id) || [];

    // Also exclude the current counterparty
    const excludedCustomerIds = [counterpartyId, ...connectedPersonCustomerIds];

    return (
        <Box>
            <Grid container direction={"row"}>
                <Grid container direction={"column"} xs>
                    <Typography variant={"h6"} sx={{ mb: 2 }}>
                        {t("clients.passportDialogCompany.basicDataFormTitle")}
                    </Typography>
                    {isLoading ? (
                        <Placeholder count={8} />
                    ) : (
                        <>
                            <Grid container direction={"column"} spacing={2}>
                                <Grid item>
                                    <TextField
                                        {...register("counterpartyNumber")}
                                        fullWidth
                                        label={t(
                                            "clients.passportDialogCompany.counterpartyNumber",
                                        )}
                                        size="small"
                                        disabled
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("companyName")}
                                        label={`${t("clients.passportDialogCompany.name")}*`}
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("companyShortName")}
                                        label={`${t("clients.passportDialogCompany.shortName")}*`}
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("nip")}
                                        label={`${t("clients.passportDialogCompany.nip")}*`}
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("regon")}
                                        label={t("clients.passportDialogCompany.regon")}
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("email")}
                                        label={`${t("clients.passportDialogCompany.primary_email")}*`}
                                        fullWidth
                                        size="small"
                                        InputProps={counterpartyData?.company?.id ? {
                                            endAdornment: 
                                            <InputAdornment position="end">
                                                <IconButton  onClick={() => {
                                                            openAdditionalEmailsDialog({
                                                                Component: AdditionalEmailsDialog,
                                                                componentProps: {
                                                                    companyId: counterpartyData?.company?.id,
                                                                },
                                                            });
                                                        }}>
                                                    <MailIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        } : null}
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("phone")}
                                        label={t("clients.passportDialogCompany.primary_phone")}
                                        fullWidth
                                        size="small"
                                        InputProps={counterpartyData?.company?.id ? {
                                            endAdornment: 
                                            <InputAdornment position="end">
                                                <IconButton  onClick={() => {
                                                            openAdditionalPhonesDialog({
                                                                Component: AdditionalPhonesDialog,
                                                                componentProps: {
                                                                    companyId: counterpartyData?.company?.id,
                                                                },
                                                            });
                                                        }}>
                                                    <LocalPhoneIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        } : null}
                                    />
                                </Grid>
                                <Grid item>
                                    <ControlledSelect
                                        control={control}
                                        label={t("clients.passportDialogCompany.legalForm")}
                                        name="companyType"
                                        data={COMPANY_LEGAL_TYPES.map((val) => ({
                                            label: val,
                                            value: val,
                                        }))}
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("company_citizenship")}
                                        label={t("clients.passportDialogCompany.companyCitizenship")}
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}
                </Grid>
                <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                <Grid container direction={"column"} xs>
                    <Box sx={{ display: "flex", alignItems: "center",justifyContent: "center" }}>
                        <Typography variant={"h6"} sx={{ mb: 2 }}>
                            {t("clients.passportDialogCompany.addressFormTitle")}
                        </Typography>
                        <AddressCopyButton
                            form={{ watch, setValue }}
                            sourcePrefix=""
                            targetSuffix="Mailing"
                            fields={[
                                "street",
                                "houseNumber",
                                "apartmentNumber",
                                "city",
                                "postalCode",
                                "country",
                            ]}
                            tooltipText={t("clients.passportDialogCompany.copyAddress")}
                            sx={{ ml: 1, mb: 2 }}
                        />
                    </Box>
                    {isLoading ? (
                        <Placeholder count={6} />
                    ) : (
                        <>
                            <Grid container direction={"column"} spacing={2}>
                                <Grid item>
                                    <TextField
                                        {...register("street")}
                                        fullWidth
                                        label={t("clients.passportDialogCompany.street")}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("houseNumber")}
                                        fullWidth
                                        label={t("clients.passportDialogCompany.buildingNumber")}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("apartmentNumber")}
                                        fullWidth
                                        label={t("clients.passportDialogCompany.apartmentNumber")}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("city")}
                                        fullWidth
                                        label={t("clients.passportDialogCompany.city")}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("postalCode")}
                                        fullWidth
                                        label={t("clients.passportDialogCompany.postalCode")}
                                        size="small"
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("country")}
                                        fullWidth
                                        label={t("clients.passportDialogCompany.country")}
                                        size="small"
                                        defaultValue="Polska"
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}
                </Grid>
                <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                <Grid container direction={"column"} xs>
                    <Typography variant={"h6"} sx={{ mb: 2 }}>
                        {t("clients.passportDialogCompany.addressMailingFormTitle")}
                    </Typography>
                    {isLoading ? (
                        <Placeholder count={6} />
                    ) : (
                        <>
                            <Grid container direction={"column"} spacing={2}>
                                <Grid item>
                                    <TextField
                                        {...register("streetMailing")}
                                        fullWidth
                                        label={t("clients.passportDialogCompany.street")}
                                        size="small"
                                        InputLabelProps={{shrink: watch("streetMailing")}}
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("houseNumberMailing")}
                                        fullWidth
                                        label={t("clients.passportDialogCompany.buildingNumber")}
                                        size="small"
                                        InputLabelProps={{shrink: watch("houseNumberMailing")}}
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("apartmentNumberMailing")}
                                        fullWidth
                                        label={t("clients.passportDialogCompany.apartmentNumber")}
                                        size="small"
                                        InputLabelProps={{shrink: watch("apartmentNumberMailing")}}
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("cityMailing")}
                                        fullWidth
                                        label={t("clients.passportDialogCompany.city")}
                                        size="small"
                                        InputLabelProps={{shrink: watch("cityMailing")}}
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("postalCodeMailing")}
                                        fullWidth
                                        label={t("clients.passportDialogCompany.postalCode")}
                                        size="small"
                                        InputLabelProps={{shrink: watch("postalCodeMailing")}}
                                    />
                                </Grid>
                                <Grid item>
                                    <TextField
                                        {...register("countryMailing")}
                                        fullWidth
                                        label={t("clients.passportDialogCompany.country")}
                                        size="small"
                                        defaultValue="Polska"
                                        InputLabelProps={{shrink: watch("countryMailing")}}
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}
                </Grid>
                <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />
                <Grid container direction={"column"} xs>
                    <Typography variant={"h6"} sx={{ mb: 2 }}>
                        {t("clients.passportDialogCompany.additionalInfo")}
                    </Typography>
                    <Grid container direction={"column"}>
                        {isLoading ? (
                            <Placeholder count={4} />
                        ) : (
                            <>
                                <CompanyAddConnectedPersons
                                    // @ts-ignore
                                    control={control}
                                    // @ts-ignore
                                    setValue={setValue}
                                    excludedCustomerIds={excludedCustomerIds}
                                />
                                <Typography
                                    variant={"body1"}
                                    sx={{
                                        fontWeight: "medium",
                                        mt: 3,
                                        textAlign: "start",
                                    }}
                                >
                                    {t("clients.passportDialogCompany.counterpartyNote")}
                                </Typography>
                                <Grid container justifyContent={"start"}>
                                    <TextField
                                        {...register("note")}
                                        fullWidth
                                        label={t("table.contacts.note")}
                                        InputProps={{
                                            rows: 3,
                                            multiline: true,
                                            inputComponent: "textarea",
                                        }}
                                    />
                                </Grid>
                                <Typography
                                    variant={"body1"}
                                    sx={{
                                        fontWeight: "medium",
                                        mt: 3,
                                        textAlign: "start",
                                    }}
                                >
                                    {t("clients.passportDialogCompany.attachments")}
                                </Typography>
                                <Grid container justifyContent={"start"}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<InventoryIcon />}
                                        fullWidth
                                        onClick={() => {
                                            openClientFilesDialog({
                                                Component: ClientFilesDialog,
                                                componentProps: {
                                                    clientId: props.counterpartyId,
                                                    clientName: `${
                                                        counterpartyData?.company?.name || ""
                                                    }`.trim(),
                                                },
                                            });
                                        }}
                                    >
                                        {t("clients.ClientPassportDialog.viewAttachments")}
                                    </Button>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Grid>
            </Grid>
            <LoadingButton
                loading={isPending}
                disabled={!enabledBtn}
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
                startIcon={<EditNoteIcon />}
                onClick={handleSubmit(onSubmit, onFormErrors)}
            >
                {t("clients.passportDialogCompany.saveChanges")}
            </LoadingButton>
        </Box>
    );
};

const counterpartyPassportFormSchema = z
    .object({
        // Dane podstawowe
        counterpartyNumber: z.string().optional(),
        companyName: z.string({ message: FORM_ERR.name_required }).nonempty({
            message: FORM_ERR.name_required,
        }),
        companyShortName: z.string({ message: FORM_ERR.company_short_name_required }).nonempty({
            message: FORM_ERR.company_short_name_required,
        }),
        nip: z.string().optional(),
        regon: z.string().optional(),
        email: z.preprocess(
            (val) => (val === "" ? undefined : val),
            z.string().email(t("form.validations.incorrect_email")).optional().nullable(),
        ),
        phone: z.string().optional(),
        companyType: z.string().optional(),
        company_citizenship: z.string().optional(),

        // Company address
        street: z.string().optional(),
        houseNumber: z.string().optional(),
        apartmentNumber: z.string().optional(),
        city: z.string().optional(),
        postalCode: z.string().optional(),
        country: z.string().optional(),

        // Company mailing address
        streetMailing: z.string().optional(),
        houseNumberMailing: z.string().optional(),
        apartmentNumberMailing: z.string().optional(),
        cityMailing: z.string().optional(),
        postalCodeMailing: z.string().optional(),
        countryMailing: z.string().optional(),

        connectedPersons: z.array(connectedCompanyPersonFormSchema).default([]),

        note: z.string().optional(),
    })
    .superRefine((data, ctx) => {
        if (data.country === "Polska") {
            const phoneRegex = /^\+48\s?\d{3}\s?\d{3}\s?\d{3}$/;
            if (data.phone && !phoneRegex.test(data.phone)) {
                ctx.addIssue({
                    message: t("form.validations.incorrect_phone"),
                    code: z.ZodIssueCode.custom,
                });
            }
            const nipRegex = /^\d{10}$/;
            if (data.nip && !nipRegex.test(data.nip)) {
                ctx.addIssue({
                    message: t("form.validations.incorrect_nip"),
                    code: z.ZodIssueCode.custom,
                });
            }
            const regonRegex = /^\d{9}$/;
            if (data.regon && !regonRegex.test(data.regon)) {
                ctx.addIssue({
                    message: t("form.validations.incorrect_regon"),
                    code: z.ZodIssueCode.custom,
                });
            }
            const zipCodeRegex = /^\d{2}-\d{3}$/;
            if (data.postalCode && !zipCodeRegex.test(data.postalCode)) {
                ctx.addIssue({
                    message: t("form.validations.incorrect_zipcode"),
                    code: z.ZodIssueCode.custom,
                });
            }
            if (data.postalCodeMailing && !zipCodeRegex.test(data.postalCodeMailing)) {
                ctx.addIssue({
                    message: t("form.validations.incorrect_zipcode"),
                    code: z.ZodIssueCode.custom,
                });
            }
        }
        return z.NEVER;
    });

const formDataFromCounterparty = (counterparty) => ({
    counterpartyNumber: counterparty.customer_number.toString(),
    companyName: counterparty.company?.name || "",
    companyShortName: counterparty.company?.name_short || "",
    nip: counterparty.company?.nip || "",
    regon: counterparty.company?.regon || "",
    email: counterparty.company?.email.email_address || "",
    phone: counterparty.company?.phone.phone_number || "",
    companyType: counterparty.company?.legal || "",
    company_citizenship: counterparty.company?.company_citizenship || "",

    // Living address
    street: counterparty.company?.address?.street || "",
    houseNumber: counterparty.company?.address?.building || "",
    apartmentNumber: counterparty.company?.address?.flat_nr || "",
    city: counterparty.company?.address?.city || "",
    postalCode: counterparty.company?.address?.zip_code || "",

    // Living address
    streetMailing: counterparty.company?.mailing_address?.street || "",
    houseNumberMailing: counterparty.company?.mailing_address?.building || "",
    apartmentNumberMailing: counterparty.company?.mailing_address?.flat_nr || "",
    cityMailing: counterparty.company?.mailing_address?.city || "",
    postalCodeMailing: counterparty.company?.mailing_address?.zip_code || "",

    // TODO
    // @ts-ignore
    connectedPersons:
        counterparty.company?.related_persons.map((person) => ({
            id: person.id,
            companyFunction: person.role,
            exisitngClientPersonId: person.person_id,
            existingClientId: person.customer_id,
            existingClient: !!person.customer_id,
            name: person.name,
            surname: person.surname,
            phone: person.phone,
            email: person.email,
        })) || [],

    note: counterparty.note || "",
});

const formDataToRequestBody = (counterpartyId, formData) => {
    return {
        customerId: counterpartyId,
        name: formData.companyName,
        name_short: formData.companyShortName,
        nip: formData.nip,
        regon: formData.regon,
        email: formData.email ?? undefined,
        phone: formData.phone,
        legal: formData.companyType,
        company_citizenship: formData.company_citizenship,
        street: formData.street || "",
        building: formData.houseNumber || "",
        flat_nr: formData.apartmentNumber || "",
        city: formData.city || "",
        zip_code: formData.postalCode || "",
        note: formData.note,
        phone_note: "",
        mailing_address : (
            formData.cityMailing || 
            formData.streetMailing || 
            formData.houseNumberMailing || 
            formData.apartmentNumberMailing || 
            formData.postalCodeMailing
        ) ? {
            street: formData.streetMailing,
            building: formData.houseNumberMailing,
            flat_nr: formData.apartmentNumberMailing,
            city: formData.cityMailing,
            zip_code: formData.postalCodeMailing,
        } : null,
        related_persons: formData.connectedPersons.map((person) => {
            if (person.exisitngClientPersonId) {
                return {
                    person_id: person.exisitngClientPersonId,
                    role: person.companyFunction,
                };
            }
            return {
                role: person.companyFunction,
                person: {
                    email: {
                        email_address: person.email || "",
                    },
                    phone: {
                        phone_number: person.phone || "",
                    },
                    person_name: person.name || "",
                    person_surname: person.surname || "",
                },
            };
        }),
    };
};

const Placeholder = (props) => {
    const { count } = props;
    return (
        <Grid container direction={"column"} gap={2}>
            {Array.from({ length: count }).map((_) => (
                <Skeleton variant="rounded" width={"100%"} height={40} />
            ))}
        </Grid>
    );
};
